// ============================================
// ProductTableRow Component
// Single row renderer for products table
// ============================================

import { EditIcon, TrashIcon } from '../../../components/icons';
import { useI18n } from '../../../i18n';
import { useMoney } from '../../../providers/CurrencyProvider';

// ============================================
// Types
// ============================================

export type StockStatusType = 'ACTIVE' | 'INACTIVE';

export interface ProductItem {
  id: string;
  imageUrl?: string;
  productCode: string;
  barcode: string;
  name: string;
  categoryPath: string;
  price: number;
  costPrice: number;
  // Bug #52: `vatRate` đã gỡ — API không có trường thuế theo sản phẩm, giá trị
  // cũ là hằng số 10 gán cứng ở mapProductToItem. Xem ghi chú ở ProductTable.
  // Bỏ `stock` / `initialStock` (09/09/2026): tồn kho chỉ còn hiện ở trang
  // Inventory. Danh sách sản phẩm là danh mục mặt hàng — tồn là con số theo
  // TỪNG CỬA HÀNG, mà màn này không chọn cửa hàng nào cả, nên mọi con số hiện ở
  // đây đều mập mờ "tồn của ai".
  status: StockStatusType;
  /**
   * Nhãn hạn chế 18+ hiển thị cạnh tên, vd `'ALCOHOL 5.2%'`, `'ALCOHOL bia'`,
   * `'TOBACCO'`. `undefined` = không phải hàng hạn chế.
   *
   * Có nhãn này thì nhìn danh sách là biết ngay sản phẩm nào khai thiếu nồng độ —
   * không có thì phải mở từng modal ra xem, và một cửa hàng vài trăm mặt hàng
   * thì việc đó không ai làm.
   */
  restrictionLabel?: string;
  /**
   * Rượu (không phải bia) mà CHƯA KHAI nồng độ.
   *
   * Backend coi chưa khai là quá mạnh và sẽ chặn bán ở cửa hàng tự phục vụ PL,
   * nên đây là lỗi dữ liệu cần sửa chứ không phải thông tin trung tính — tô đỏ.
   */
  missingAbv?: boolean;
}

interface ProductTableRowProps {
  item: ProductItem;
  onEdit?: (item: ProductItem) => void;
  onDelete?: (item: ProductItem) => void;
}

// ============================================
// Cell Components
// ============================================

function ImageCell({ imageUrl, productName }: { imageUrl?: string; productName: string }) {
  return (
    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
      {imageUrl ? (
        <img src={imageUrl} alt={productName} className="h-full w-full object-cover" />
      ) : (
        <span className="text-[10px] font-medium text-slate-400">IMG</span>
      )}
    </div>
  );
}

function ProductCodeCell({ code }: { code: string }) {
  return <span className="font-mono text-sm font-semibold text-slate-700">{code}</span>;
}

function BarcodeCell({ barcode }: { barcode: string }) {
  return <span className="text-sm text-slate-500">{barcode}</span>;
}

function ProductNameCell({ name, supplier }: { name: string; supplier: string }) {
  return (
    <div>
      <p className="text-sm font-semibold text-slate-900">{name}</p>
      <p className="text-xs text-slate-400">{supplier}</p>
    </div>
  );
}

function PriceCell({ price }: { price: number }) {
  const money = useMoney();
  return (
    <span className="text-sm font-semibold text-slate-900">
      {money.format(price, { minimumFractionDigits: 2 })}
    </span>
  );
}

function StatusBadge({ status }: { status: StockStatusType }) {
  const { t } = useI18n();
  if (status === 'ACTIVE') {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
        {t('productsActive')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-500">
      {t('productsInactive')}
    </span>
  );
}

function ActionButtons({
  onEdit,
  onDelete,
  onTranslations,
}: {
  onEdit: () => void;
  onDelete: () => void;
  /** Mở màn nhập bản dịch (sheet 2 #15). Bỏ trống thì nút không hiện. */
  onTranslations?: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {onTranslations && (
        <button
          onClick={(e) => { e.stopPropagation(); onTranslations(); }}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-[10px] font-bold text-slate-500 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600"
          title="Translations"
        >
          A文
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600"
      >
        <EditIcon size={15} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
      >
        <TrashIcon size={15} />
      </button>
    </div>
  );
}


// ============================================
// Export Cell Components
// ============================================

export const ProductCells = {
  ImageCell,
  ProductCodeCell,
  BarcodeCell,
  ProductNameCell,
  PriceCell,
  StatusBadge,
  ActionButtons,
};

// ============================================
// Component
// ============================================

export function ProductTableRow({ item, onEdit, onDelete }: ProductTableRowProps) {
  return (
    <tr className="group border-t border-slate-100 transition-colors hover:bg-slate-50">
      <td className="px-4 py-3">
        <ImageCell imageUrl={item.imageUrl} productName={item.name} />
      </td>
      <td className="px-4 py-3">
        <ProductCodeCell code={item.productCode} />
      </td>
      <td className="px-4 py-3">
        <BarcodeCell barcode={item.barcode} />
      </td>
      <td className="px-4 py-3">
        <ProductNameCell name={item.name} supplier={item.categoryPath} />
        {item.restrictionLabel ? (
          <span
            className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
              item.missingAbv
                ? 'bg-red-50 text-red-700'
                : 'bg-amber-50 text-amber-700'
            }`}
            // Nồng độ chưa khai không phải chuyện hiển thị — sản phẩm sẽ bị cổng
            // từ chối bán. Cho tooltip nói rõ hậu quả thay vì chỉ tô màu.
            title={item.missingAbv ? 'Chưa khai nồng độ — sẽ bị chặn bán ở cửa hàng tự phục vụ' : undefined}
          >
            {item.restrictionLabel}
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3">
        <PriceCell price={item.price} />
      </td>
      <td className="px-4 py-3">
        <PriceCell price={item.costPrice} />
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={item.status} />
      </td>
      <td className="px-4 py-3">
        <ActionButtons
          onEdit={() => onEdit?.(item)}
          onDelete={() => onDelete?.(item)}
        />
      </td>
    </tr>
  );
}
